import { useEffect, useState } from 'react';
import {
  FlightDetails,
  FlightDetailsResponse,
  Outbound,
} from 'ts/flight/interfaces/FlightDetailsResponse';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getFlight } from 'services/flight';
import * as idb from 'utils/shared/idbUrl';
import useFlightDataLayer from './useFlightDataLayer';
import { b2b } from 'constants/generic';

const useFlightDetails = (idbKey: string, isPair?: boolean) => {
  const [urlParams] = useSearchParams();
  const { pushFlightProductDetails } = useFlightDataLayer();

  const [flight, setFlight] = useState<FlightDetails>();
  const [response, setResponse] = useState<FlightDetailsResponse>();
  const [bounds, setBounds] = useState<Outbound[]>([]);
  const [flexFlightId, setFlexFlightId] = useState('');
  const flexOrFlight = flexFlightId
    ? response?.flexDetails?.listOfFlexFlights.find((fl) => fl.id === flexFlightId)!
    : flight;

  const { pathname } = useLocation();
  const [boundsPair, setBoundsPair] = useState<Outbound[]>([]);
  const [flightPair, setFlightPair] = useState<FlightDetails>();
  const [flexFlightIdPair, setFlexFlightIdPair] = useState('');
  const flexOrFlightPair = flexFlightIdPair
    ? response?.flexDetailsPair?.listOfFlexFlights.find((fl) => fl.id === flexFlightIdPair)!
    : flightPair;

  useEffect(() => {
    if (flexFlightId && response?.flexDetails) {
      let bounds: Outbound[] = [];

      const fFlight = response.flexDetails.listOfFlexFlights.find((fl) => fl.id === flexFlightId);
      if (fFlight?.outbound && fFlight?.inbound) {
        bounds = [{ ...fFlight.outbound }, { ...fFlight.inbound, flightType: 'return' }];
      } else if (fFlight?.outbound) {
        bounds = [{ ...fFlight.outbound }];
      } else if (fFlight) {
        bounds = fFlight?.flightBounds;
      }

      setBounds(bounds);
    }
  }, [flexFlightId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (flexFlightIdPair && response?.flexDetailsPair) {
      let boundsPair: Outbound[] = [];

      const fFlight = response.flexDetailsPair.listOfFlexFlights.find(
        (fl) => fl.id === flexFlightId
      );
      if (fFlight?.outbound && fFlight?.inbound) {
        boundsPair = [{ ...fFlight.outbound }, { ...fFlight.inbound, flightType: 'return' }];
      } else if (fFlight?.outbound) {
        boundsPair = [{ ...fFlight.outbound }];
      } else if (fFlight) {
        boundsPair = fFlight?.flightBounds;
      }

      setBoundsPair(boundsPair);
    }
  }, [flexFlightIdPair]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (b2b) {
      const allowedPaths = ['/flight/details', '/multiflight/details'];
      if (!allowedPaths.includes(pathname.toLowerCase())) {
        return;
      }
    }
    (async () => {
      const pageState = await idb.getPageState(idbKey);
      if (pageState && pageState.response) {
        setFlexFlightId(pageState.flexFlightId);
        setBounds(pageState.bounds);
        setResponse(pageState.response);
        const newFlight = pageState.response?.flight || pageState.response?.flexDetails?.flight;
        setFlight(newFlight);

        if (isPair) {
          setFlexFlightIdPair(pageState.flexFlightIdPair);
          setBoundsPair(pageState.boundsPair);
          const newFlightPair =
            pageState.response?.flightPair || pageState.response?.flexDetailsPair?.flight;
          setFlightPair(newFlightPair);
        }

        idb.deletePageState(idbKey);

        return;
      }

      setResponse(undefined);
      setFlight(undefined);
      setFlightPair(undefined);
      setBounds([]);
      setBoundsPair([]);

      const id = urlParams.get('id');
      const searchToken = urlParams.get('searchToken');
      const pairId = urlParams.get('pairId');
      const isSale = urlParams.get('isSale');

      const params: any = {
        id,
        isSale: isSale === 'true' ? true : undefined,
      };

      if (pairId) {
        params.pairId = pairId;
        params.pairSearchToken = urlParams.get('pairSearchToken');
      }

      if (searchToken) {
        params.searchToken = searchToken;
      }

      if ((searchToken || isSale) && id) {
        const newResponse = await getFlight(params);

        if (newResponse) {
          const newFlight = newResponse?.flight || newResponse?.flexDetails?.flight;
          setFlight(newFlight);
          setResponse(newResponse);
          pushFlightProductDetails(newFlight);

          let bounds: Outbound[] = [];
          if (newFlight?.outbound && newFlight?.inbound) {
            bounds = [{ ...newFlight.outbound }, { ...newFlight.inbound, flightType: 'return' }];
          } else if (newFlight?.outbound) {
            bounds = [{ ...newFlight.outbound }];
          } else if (newFlight) {
            bounds = newFlight?.flightBounds;
          }

          setBounds(bounds);

          if (isPair) {
            const newFlightPair = newResponse?.flightPair || newResponse?.flexDetailsPair?.flight;
            setFlightPair(newFlightPair);

            let boundsPair: Outbound[] = [];
            if (newFlightPair?.outbound && newFlightPair?.inbound) {
              boundsPair = [
                { ...newFlightPair.outbound },
                { ...newFlightPair.inbound, flightType: 'return' },
              ];
            } else if (newFlightPair?.outbound) {
              boundsPair = [{ ...newFlightPair.outbound }];
            } else if (newFlightPair) {
              boundsPair = newFlightPair?.flightBounds;
            }

            setBoundsPair(boundsPair);
          }
        }
      }
    })();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    response,
    flight,
    flexFlightId,
    setFlexFlightId,
    flexOrFlight,
    bounds,
    setResponse,
    setBounds,

    flightPair,
    flexFlightIdPair,
    setFlexFlightIdPair,
    flexOrFlightPair,
    boundsPair,
  };
};

export default useFlightDetails;
